import React, { Component } from "react";
import { Alert, Button, Spin, Tag } from "antd";
import ReCAPTCHA from "react-google-recaptcha";

import { GetData } from "../../api/service";
import {
  RECAPTCHA_SITE_KEY,
  isValidEmail,
  isValidIndianPhoneNumber
} from "../../Helper/GlobalConstants";
import {
  CARD_FIELDS,
  TERM_OPTIONS,
  PHOTO_FIELD,
  PHOTO_LIMIT_KB,
  hasPhoto,
  missingCardFields,
  getGoodClass
} from "./cardFields";
import FileUpload from "../Grievance/FileUpload";

/**
 * Step 2 — the details that will be printed on the card.
 *
 * Every field here goes onto the card verbatim, so the controls are constrained
 * rather than free text: the course code drives the Class line and must match a
 * known code, the term must be a real semester (a large share of student rows
 * carry 0, which prints nothing), and the date of birth must serialise as
 * YYYY-MM-DD or the card renders "Invalid date".
 */
export default class IdCardForm extends Component {
  state = {
    types: [],
    courses: [],
    id_card_type_id: "",
    session: "",
    reason: "",
    recaptcha_token: "",
    message: "",
    isLoadingTypes: true
  };

  componentDidMount() {
    GetData("/getidcardtype").then((resp) => {
      this.setState({
        types: Array.isArray(resp) ? resp : [],
        isLoadingTypes: false
      });
    });

    GetData("/getcourse").then((resp) => {
      this.setState({ courses: Array.isArray(resp) ? resp : [] });
    });
  }

  selectedType = () => {
    return this.state.types.filter(
      (el) => String(el.id) === String(this.state.id_card_type_id)
    )[0];
  };

  isReprint = () => {
    let type = this.selectedType();
    return type && type.type_code === "REPRINT";
  };

  /**
   * The course master may not list every code already in use on student rows,
   * so a prefilled value is always offered even when the master lacks it.
   */
  courseOptions = () => {
    let codes = this.state.courses.map((el) => el.course_code);
    let current = this.props.form.course_code;

    if (current && codes.indexOf(current) === -1) {
      codes = [current].concat(codes);
    }

    return codes;
  };

  renderControl = (f) => {
    let value = this.props.form[f.name] === undefined ? "" : this.props.form[f.name];

    if (f.control === "course") {
      return (
        <select
          className="form-control"
          name={f.name}
          value={value}
          onChange={this.props.onFieldChange}
        >
          <option value="">Select your course</option>
          {this.courseOptions().map((code, i) => (
            <option key={i} value={code}>
              {code}
            </option>
          ))}
        </select>
      );
    }

    if (f.control === "term") {
      return (
        <select
          className="form-control"
          name={f.name}
          value={value}
          onChange={this.props.onFieldChange}
        >
          <option value="">Select</option>
          {TERM_OPTIONS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      );
    }

    if (f.control === "date") {
      // a native date input already emits YYYY-MM-DD, which is exactly what the
      // card's moment(dob, "YYYY-MM-DD") expects
      return (
        <input
          type="date"
          className="form-control"
          name={f.name}
          value={value}
          onChange={this.props.onFieldChange}
        />
      );
    }

    return (
      <input
        type={f.type || "text"}
        className="form-control"
        name={f.name}
        maxLength={f.maxLength}
        value={value}
        onChange={this.props.onFieldChange}
      />
    );
  };

  validate = () => {
    if (this.state.id_card_type_id === "") {
      return "Please choose Print or Reprint";
    }

    let missing = missingCardFields(this.props.form);
    if (missing.length > 0) {
      return "Please fill in: " + missing.map((f) => f.label).join(", ");
    }

    if (!isValidEmail(this.props.form.email)) {
      return "Please enter a valid email address";
    }

    if (!isValidIndianPhoneNumber(this.props.form.phone)) {
      return "Please enter a valid 10 digit contact number";
    }

    if (!hasPhoto(this.props.form)) {
      return "Please upload your passport size photo";
    }

    if (this.isReprint() && this.state.reason.trim() === "") {
      return "Please tell us why you need a reprint";
    }

    if (this.state.recaptcha_token === "") {
      return "Please complete the captcha";
    }

    return "";
  };

  onSubmit = () => {
    let error = this.validate();
    if (error !== "") {
      this.setState({ message: error });
      return;
    }

    this.setState({ message: "" });

    this.props.onSubmit({
      ...this.props.form,
      id_card_type_id: this.state.id_card_type_id,
      session: this.state.session,
      reason: this.state.reason,
      recaptcha_token: this.state.recaptcha_token
    });
  };

  render() {
    let missing = missingCardFields(this.props.form).map((f) => f.name);

    return (
      <div>
        <h5>1. What do you need?</h5>
        {this.state.isLoadingTypes ? (
          <Spin />
        ) : this.state.types.length === 0 ? (
          <Alert
            type="error"
            showIcon
            message="ID card applications are not open yet. Please contact the college office."
          />
        ) : (
          <div className="form-group">
            {this.state.types.map((el, i) => (
              <div className="radio" key={i}>
                <label>
                  <input
                    type="radio"
                    name="id_card_type_id"
                    checked={String(this.state.id_card_type_id) === String(el.id)}
                    onChange={() => this.setState({ id_card_type_id: el.id })}
                  />
                  &nbsp; {el.type_name} &mdash; &#8377; {el.registration_fee}
                </label>
              </div>
            ))}
          </div>
        )}

        <br />

        <h5>2. Details printed on your card</h5>
        <Alert
          type="info"
          showIcon
          message="Check these carefully — they print on the card exactly as shown."
        />
        <br />

        <div className="row">
          {CARD_FIELDS.map((f, i) => (
            <div className="col-md-4" key={i}>
              <div className="form-group">
                <label>
                  {f.label} *{" "}
                  {missing.indexOf(f.name) > -1 && (
                    <Tag color="orange" style={{ marginLeft: 4 }}>
                      needed
                    </Tag>
                  )}
                </label>
                {this.renderControl(f)}
              </div>
            </div>
          ))}

          <div className="col-md-4">
            <div className="form-group">
              <label>Session</label>
              <input
                type="text"
                className="form-control"
                maxLength={100}
                placeholder="2025-26"
                value={this.state.session}
                onChange={(e) => this.setState({ session: e.target.value })}
              />
            </div>
          </div>
        </div>

        <div
          style={{
            background: "#fafafa",
            padding: 10,
            marginBottom: 12,
            borderRadius: 4
          }}
        >
          <b>Class line as it will be printed: </b>
          {getGoodClass(this.props.form.course_code, this.props.form.term) || (
            <i>select your course and semester</i>
          )}
        </div>

        <h5>3. Your photo</h5>
        <Alert
          type="info"
          showIcon
          message="This exact photo is printed on your card. Use a plain passport size photo of your face, .jpg or .png, under 300KB."
        />
        <br />

        <div className="row">
          <div className="col-md-4">
            <div className="form-group">
              <label>
                Passport Size Photo *{" "}
                {!hasPhoto(this.props.form) && (
                  <Tag color="orange" style={{ marginLeft: 4 }}>
                    needed
                  </Tag>
                )}
              </label>
              <FileUpload
                type={`ID_CARD_PHOTO`}
                limit={PHOTO_LIMIT_KB}
                data={this.props.form[PHOTO_FIELD]}
                onChange={(e) => this.props.onPhotoChange(e)}
              />
            </div>
          </div>
        </div>

        {this.isReprint() && (
          <div className="form-group">
            <label>Why do you need a reprint? *</label>
            <textarea
              className="form-control"
              rows={2}
              value={this.state.reason}
              onChange={(e) => this.setState({ reason: e.target.value })}
              placeholder="Lost / damaged / details changed"
            />
          </div>
        )}

        <ReCAPTCHA
          sitekey={RECAPTCHA_SITE_KEY}
          onChange={(token) => this.setState({ recaptcha_token: token || "" })}
        />
        <br />

        {this.state.message !== "" && (
          <div>
            <Alert type="error" showIcon message={this.state.message} />
            <br />
          </div>
        )}

        {this.props.isSubmitting ? (
          <Spin />
        ) : (
          <Button type="primary" onClick={this.onSubmit}>
            Submit and proceed to payment
          </Button>
        )}
      </div>
    );
  }
}
